import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
  Table,
  Badge,
} from "reactstrap";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import VoucherService from "../../../services/AccountingServices/VoucherService";
import FiscalYearService from "../../../services/AccountingServices/FiscalYearService";
import LedgerService from "../../../services/AccountingServices/LedgerService";
import Select from "react-select";

const VoucherForm = ({ isOpen, toggle, selectedItem, refetch }) => {
  const [loading, setLoading] = useState(false);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [ledgers, setLedgers] = useState([]);

  const VOUCHER_TYPES = ["JOURNAL", "INCOME", "PAYMENT"];

  useEffect(() => {
    if (isOpen) {
      fetchDropdownData();
    }
  }, [isOpen]);

  const fetchDropdownData = async () => {
    try {
      const [fyResponse, ledgerResponse] = await Promise.all([
        FiscalYearService.getList(),
        LedgerService.getList(),
      ]);
      const fyData = Array.isArray(fyResponse) ? fyResponse : fyResponse?.items || [];
      // Only show active fiscal years in the form
      const activeYears = fyData.filter(fy => fy.isActive);
      setFiscalYears(activeYears);

      // Auto-select the active fiscal year for new vouchers
      if (!formik.values.fiscalYearCode && activeYears.length > 0) {
        formik.setFieldValue("fiscalYearCode", activeYears[0].code);
      }

      const ledgersData = Array.isArray(ledgerResponse) ? ledgerResponse : ledgerResponse?.items || [];
      setLedgers(ledgersData.map(l => ({ value: l.id, label: `${l.name} (${l.code})` })));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const validationSchema = Yup.object({
    fiscalYearCode: Yup.string().required("Fiscal Year is required"),
    voucherType: Yup.string().required("Voucher Type is required"),
    valueDate: Yup.date().required("Value Date is required"),
    description: Yup.string().required("Description is required"),
    details: Yup.array().of(
      Yup.object({
        ledgerId: Yup.number().required("Ledger is required").min(1, "Select a ledger"),
        drAmount: Yup.number().min(0, "Invalid amount"),
        crAmount: Yup.number().min(0, "Invalid amount"),
        remarks: Yup.string(),
      })
    ).min(2, "At least two entries are required"),
  });

  const formik = useFormik({
    initialValues: {
      fiscalYearCode: selectedItem?.fiscalYearCode || "",
      voucherType: selectedItem?.voucherType || "JOURNAL",
      valueDate: selectedItem?.valueDate ? selectedItem.valueDate.substring(0, 10) : new Date().toISOString().substring(0, 10),
      description: selectedItem?.description || "",
      details: selectedItem?.details || [
        { ledgerId: "", drAmount: 0, crAmount: 0, remarks: "" },
        // { ledgerId: "", drAmount: 0, crAmount: 0, remarks: "" },
      ],
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      // Validate Double Entry Balance
      const totalDr = values.details.reduce((sum, item) => sum + (Number(item.drAmount) || 0), 0);
      const totalCr = values.details.reduce((sum, item) => sum + (Number(item.crAmount) || 0), 0);

      if (Math.abs(totalDr - totalCr) > 0.01) {
        toast.error(`Voucher is not balanced! Difference: ${Math.abs(totalDr - totalCr).toFixed(2)} (DR: ${totalDr.toFixed(2)}, CR: ${totalCr.toFixed(2)})`);
        return;
      }

      if (totalDr === 0) {
        toast.error("Voucher amount cannot be zero.");
        return;
      }

      try {
        setLoading(true);
        if (selectedItem?.id) {
          await VoucherService.update(selectedItem.id, values);
          toast.success("Voucher updated successfully");
        } else {
          await VoucherService.create(values);
          toast.success("Voucher created successfully");
        }
        refetch();
        toggle();
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to save voucher");
      } finally {
        setLoading(false);
      }
    },
  });
  console.log("🚀 ~ VoucherForm ~ formik:", formik)

  const addRow = () => {
    formik.setFieldValue("details", [
      ...formik.values.details,
      { ledgerId: "", drAmount: 0, crAmount: 0, remarks: "" },
    ]);
  };

  const removeRow = (index) => {
    const newDetails = [...formik.values.details];
    newDetails.splice(index, 1);
    formik.setFieldValue("details", newDetails);
  };

  const totalDr = formik.values.details.reduce((sum, item) => sum + (Number(item.drAmount) || 0), 0);
  const totalCr = formik.values.details.reduce((sum, item) => sum + (Number(item.crAmount) || 0), 0);
  const isBalanced = Math.abs(totalDr - totalCr) <= 0.01 && totalDr > 0;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl">
      <ModalHeader toggle={toggle} className="border-bottom-0 fs-18 fw-bold">
        {selectedItem?.id ? "Edit Voucher" : "Add New Voucher"}
      </ModalHeader>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <ModalBody className="py-4">
            <Row className="g-3">
              <Col lg={4}>
                <FormGroup>
                  <Label className="small text-uppercase fw-bold text-muted">Fiscal Year</Label>
                  <div className="position-relative">
                    <Input
                      type="text"
                      className="form-control border-2 bg-light fw-bold text-primary"
                      value={fiscalYears.find(fy => fy.code === formik.values.fiscalYearCode)?.name || formik.values.fiscalYearCode || "No Active Year"}
                      readOnly
                      disabled
                    />
                    <i className="ri-lock-2-line position-absolute top-50 end-0 translate-middle-y me-3 text-muted"></i>
                  </div>
                  {formik.touched.fiscalYearCode && formik.errors.fiscalYearCode && (
                    <div className="text-danger small mt-1">{formik.errors.fiscalYearCode}</div>
                  )}
                </FormGroup>
              </Col>
              <Col lg={4}>
                <FormGroup>
                  <Label className="small text-uppercase fw-bold text-muted">Voucher Type</Label>
                  <Input
                    type="select"
                    name="voucherType"
                    className="form-select border-2"
                    value={formik.values.voucherType}
                    onChange={formik.handleChange}
                  >
                    {VOUCHER_TYPES.map(vt => <option key={vt} value={vt}>{vt}</option>)}
                  </Input>
                  
                </FormGroup>
              </Col>
              <Col lg={4}>
                <FormGroup>
                  <Label className="small text-uppercase fw-bold text-muted">Value Date</Label>
                  <Input
                    type="date"
                    name="valueDate"
                    className="form-control border-2"
                    value={formik.values.valueDate}
                    onChange={formik.handleChange}
                  />
                </FormGroup>
              </Col>
              <Col lg={12}>
                <FormGroup>
                  <Label className="m-0 small text-uppercase fw-bold text-muted">Description</Label>
                  <Input
                    type="textarea"
                    name="description"
                    rows={2}
                    placeholder="Brief description of the transaction"
                    className="form-control border-2"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    invalid={formik.touched.description && !!formik.errors.description}
                  />
                  <FormFeedback type="invalid">
                        {formik.errors.description}
                      </FormFeedback>
                </FormGroup>
              </Col>
            </Row>

            <div className="mt-4 border rounded overflow-hidden">
              <Table className="mb-0 align-middle table-nowrap">
                <thead className="table-light">
                  <tr>
                    <th width="35%" className="px-3">Ledger</th>
                    <th width="15%" className="px-3">Dr Amount</th>
                    <th width="15%" className="px-3">Cr Amount</th>
                    <th width="25%" className="px-3">Remarks</th>
                    <th width="10%" className="text-center"></th>
                  </tr>
                </thead>
              <tbody>
                  {formik.values.details.map((detail, index) => (
                    <tr key={index} className="border-bottom">
                      <td className="p-2">
                        <Select
                          options={ledgers}
                          placeholder="Select Ledger..."
                          value={ledgers.find(l => l.value === detail.ledgerId)}
                          onChange={(opt) => formik.setFieldValue(`details[${index}].ledgerId`, opt?.value || "")}
                          classNamePrefix="react-select"
                          menuPortalTarget={document.body}
                          styles={{ 
                            control: (base) => ({ ...base, border: '2px solid #eff2f7', borderRadius: '6px' }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 })
                          }}

                        />
                        {formik.touched.details?.[index]?.ledgerId && formik.errors.details?.[index]?.ledgerId && (
                          <FormFeedback type="invalid" className="d-block">
                            {formik.errors.details?.[index]?.ledgerId}
                          </FormFeedback>
                        )}
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          className="form-control-sm border-2 text-end"
                          value={detail.drAmount}
                          onChange={(e) => {
                            formik.setFieldValue(`details[${index}].drAmount`, Number(e.target.value));
                            if (Number(e.target.value) > 0) formik.setFieldValue(`details[${index}].crAmount`, 0);
                          }}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          className="form-control-sm border-2 text-end"
                          value={detail.crAmount}
                          onChange={(e) => {
                            formik.setFieldValue(`details[${index}].crAmount`, Number(e.target.value));
                            if (Number(e.target.value) > 0) formik.setFieldValue(`details[${index}].drAmount`, 0);
                          }}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="text"
                          className="form-control-sm border-2"
                          placeholder="Line remarks"
                          value={detail.remarks}
                          onChange={(e) => formik.setFieldValue(`details[${index}].remarks`, e.target.value)}
                        />
                      </td>
                      <td className="p-2 text-center">
                        <Button
                          color="soft-danger"
                          size="sm"
                          disabled={formik.values.details.length <= 1}
                          onClick={() => removeRow(index)}
                        >
                          <i className="ri-delete-bin-line"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-light fw-bold text-end">
                  <tr>
                    <td className="text-start px-3">
                      <Button color="soft-primary" size="sm" type="button" onClick={addRow}>
                        <i className="ri-add-line me-1"></i> Add Entry
                      </Button>
                    </td>
                    <td className="px-3 fs-14">{totalDr.toFixed(2)}</td>
                    <td className="px-3 fs-14">{totalCr.toFixed(2)}</td>
                    <td colSpan={2} className="px-3 text-start align-middle">
                      {totalDr > 0 && (
                        <Badge color={isBalanced ? "success" : "danger"} className="fs-11 px-3">
                          {isBalanced ? "BALANCED" : `UNBALANCED (Diff: ${Math.abs(totalDr - totalCr).toFixed(2)})`}
                        </Badge>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </ModalBody>
          <ModalFooter className="border-top-0 d-flex gap-2">
            <Button color="light" size="lg" className="px-4" onClick={toggle} disabled={loading}>
              Cancel
            </Button>
            <Button color="primary" size="lg" className="px-4 shadow-sm" type="submit" disabled={loading}>
              {loading ? "Processing..." : selectedItem?.id ? "Update Voucher" : "Create Voucher"}
            </Button>
          </ModalFooter>
        </form>
      </FormikProvider>
    </Modal>
  );
};

export default VoucherForm;
